<template>
  <div class="reset-password-container">
    <v-card class="reset-password-card">
      <v-card-title class="reset-password-header">
        <v-img
          v-if="!loading"
          :src="logoLink"
          :alt="systemAppName"
          class="reset-password-logo"
          contain
          @error="onImageError"
        />
        <div v-else class="reset-password-logo skeleton-placeholder">
          <v-progress-circular indeterminate :color="primaryColor" />
        </div>
      </v-card-title>
      
      <v-card-text class="reset-password-body">
        <div v-if="loading" class="loading-skeleton">
          <v-progress-circular indeterminate :color="primaryColor" size="64" class="mb-4" />
          <div class="text-center">
            <p class="text-muted">Carregando configurações...</p>
          </div>
        </div>
        <template v-else>
          <v-alert
            v-if="successMessage"
            type="success"
            class="mb-4"
          >
            {{ successMessage }}
          </v-alert>
          

          

          
          <v-alert
            v-show="errorMessage"
            class="mb-4"
            prominent
            style="background-color: #ffebee; border-left: 4px solid #f44336; color: #c62828;"
          >
            <div class="d-flex align-center">
              <v-icon class="mr-2" style="color: #c62828;">mdi-alert-circle</v-icon>
              <div>
                <div class="font-weight-bold" style="color: #000000;">{{ errorMessage }}</div>
                <template v-if="errorMessage.includes('utilizado ou expirou')">
                  <div class="mt-3">
                    <v-btn 
                      :color="primaryColor" 
                      outlined 
                      small 
                      class="mr-2"
                      @click="$router.push('/login')"
                    >
                      <v-icon left small>mdi-arrow-left</v-icon>
                      Voltar para o Login
                    </v-btn>
                    <v-btn 
                      :color="primaryColor" 
                      outlined 
                      small
                      @click="$router.push('/forgot-password')"
                    >
                      <v-icon left small>mdi-refresh</v-icon>
                      Solicitar nova redefinição
                    </v-btn>
                  </div>
                </template>
              </div>
            </div>
          </v-alert>
          
          <!-- Só mostrar o formulário se não houver erro de token inválido -->
          <template v-if="!errorMessage || !errorMessage.includes('utilizado ou expirou')">
            <h2 class="reset-password-title">Redefinir Senha</h2>
            
            <v-form ref="form" v-model="valid" @submit.prevent="updatePassword">
              <v-text-field
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                label="Nova senha"
                :rules="passwordRules"
                required
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="showPassword = !showPassword"
              />
              
              <v-text-field
                v-model="passwordConfirmation"
                :type="showPasswordConfirmation ? 'text' : 'password'"
                label="Confirmar nova senha"
                :rules="passwordConfirmationRules"
                required
                :append-icon="showPasswordConfirmation ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="showPasswordConfirmation = !showPasswordConfirmation"
              />
              
              <v-btn
                :loading="loadingBtn"
                :disabled="!valid || loadingBtn"
                block
                :color="primaryColor"
                class="white--text mt-4"
                @click="updatePassword"
              >
                {{ loadingBtn ? 'Atualizando...' : 'Atualizar Senha' }}
              </v-btn>
            </v-form>
            
            <div class="text-center mt-4">
              <v-btn
                text
                :color="primaryColor"
                @click="$router.push('/login')"
              >
                Voltar para o Login
              </v-btn>
            </div>
          </template>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ResetPassword",
  data() {
    return {
      valid: false,
      loading: true,
      loadingBtn: false,
      showPassword: false,
      showPasswordConfirmation: false,
      password: "",
      passwordConfirmation: "",
      successMessage: "",
      errorMessage: "",
      passwordRules: [
        v => !!v || "Senha é obrigatória",
        v => v.length >= 6 || "Senha deve ter pelo menos 6 caracteres"
      ],
      passwordConfirmationRules: [
        v => !!v || "Confirmação de senha é obrigatória",
        v => v === this.password || "Senhas não coincidem"
      ],
      logoLink: require("@/assets/logo_flowit.png"),
      primaryColor: '#1976D2',
      systemAppName: 'FlowIt',
    };
  },
  computed: {
    ...mapGetters({
      systemConfiguration: "system/systemConfiguration",
      isSystemLoading: "system/isLoading",
    })
  },
  created() {
    this.fetchPartnerConfig();
  },
  methods: {
    async fetchPartnerConfig() {
      const token = this.$route.query.token;
      if (!token) {
        this.loading = false;
        this.errorMessage = "Token de redefinição inválido";
        return;
      }
      
      try {
        // Primeiro, tentar carregar via token
        const response = await this.$flowit.getPartnerInfoByToken(token);
        const config = response.data.partner;
        this.applyPartnerConfig(config);
        // Se chegou aqui, o token é válido, então limpar qualquer erro anterior
        this.$set(this, 'errorMessage', '');
      } catch (e) {
        // Fallback: tentar carregar configurações via domínio
        try {
          await this.$store.dispatch('system/fetchSystemConfiguration');
          const systemConfig = this.systemConfiguration;
          
          if (systemConfig) {
            // Converter estrutura do sistema para estrutura do parceiro
            const partnerConfig = {
              logo_url: systemConfig.logo_url,
              favicon_url: systemConfig.favicon_url,
              panel_color: systemConfig.primary_color,
              fantasy_name: systemConfig.app_name,
              name: systemConfig.app_name
            };
            this.applyPartnerConfig(partnerConfig);
          }
        } catch (fallbackError) {
          // Silenciar erro do fallback
        }
        
        // Definir mensagem de erro para token inválido
        this.$set(this, 'errorMessage', "Este link de redefinição de senha já foi utilizado ou expirou. Solicite um novo.");
      } finally {
        this.loading = false;
      }
    },
    
    applyPartnerConfig(config) {
      // Aplicar logo
      if (config.logo_url) {
        this.logoLink = config.logo_url;
      }
      
      // Aplicar favicon
      if (config.favicon_url) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        const currentDomain = window.location.origin;
        const faviconUrl = config.favicon_url.replace('http://localhost:3000', currentDomain);
        link.href = faviconUrl;
      }
      
      // Aplicar cor primária
      if (config.panel_color) {
        this.primaryColor = config.panel_color;
        document.documentElement.style.setProperty('--v-primary-base', config.panel_color);
      }
      
      // Aplicar nome fantasia
      this.systemAppName = config.fantasy_name || config.name || 'FlowIt';
    },
    async updatePassword() {
      if (!this.$refs.form.validate()) {
        return;
      }
      const token = this.$route.query.token;
      if (!token) {
        this.errorMessage = "Token de redefinição inválido";
        return;
      }
      this.loadingBtn = true;
      this.errorMessage = "";
      this.successMessage = "";
      try {
        const response = await this.$flowit.updatePasswordByToken({
          token: token,
          password: this.password,
          password_confirmation: this.passwordConfirmation
        });
        
        this.successMessage = "Senha atualizada com sucesso! Redirecionando...";
        setTimeout(() => {
          if (response && response.data && response.data.redirect_url) {
            window.location.href = response.data.redirect_url;
          } else {
            this.$router.push("/login");
          }
        }, 2000);
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.error &&
          error.response.data.error.includes("Token inválido")
        ) {
          this.errorMessage = "Este link de redefinição de senha já foi utilizado ou expirou. Solicite um novo.";
        } else {
          this.errorMessage = (error.response && error.response.data && error.response.data.error) || "Erro ao atualizar senha";
        }
      } finally {
        this.loadingBtn = false;
      }
    },
    onImageError() {
      // Fallback para logo padrão se a imagem falhar
      this.logoLink = require("@/assets/logo_flowit.png");
    }
  }
};
</script>

<style scoped>
.reset-password-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--v-primary-base, #1976D2) 0%, rgba(25, 118, 210, 0.8) 100%);
  padding: 20px;
}

.reset-password-card {
  max-width: 400px;
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.reset-password-header {
  text-align: center;
  padding: 24px 24px 0;
  background: #ffffff;
  border-radius: 12px 12px 0 0;
}

.reset-password-logo {
  max-width: 120px;
  max-height: 60px;
  margin: 0 auto;
}

.skeleton-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px;
}

.reset-password-body {
  padding: 24px;
}

.reset-password-title {
  text-align: center;
  margin-bottom: 24px;
  color: var(--v-primary-base, #1976D2);
  font-weight: 600;
}

.loading-skeleton {
  padding: 20px 0;
}

@media (max-width: 600px) {
  .reset-password-container {
    padding: 10px;
  }
  
  .reset-password-card {
    max-width: 100%;
  }
}
</style> 